import { useAuth } from 'components/Auth'
import { differenceInDays } from 'contexts/util'
import { createContext, useState } from 'react'
import { useQuery } from 'react-query'
import { fetchUserDetail } from 'services/user'

import style from './style.module.scss'
import CollapsibleBanner from 'components/CollapsibleBanner'
import { useAffiliate } from 'hooks/useAffiliate'
import { AffiliatePaymentStatus, Role } from 'types'
import { Icon } from '@crossfit/ui'
import { Text } from '@crossfit/design-system'
import Hide from './hide.svg'
import classNames from 'classnames'

export type BannerContext = {
  alert90Days: boolean
  alertCredentialExpired: boolean
}
export const BannerContext = createContext({} as BannerContext)

export function BannerContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { user } = useAuth()
  const { selectedAffiliate: affiliate } = useAffiliate()
  const userDetail = useQuery(
    ['userDetail', user?.id],
    () => fetchUserDetail(user?.id, true),
    { enabled: !!user?.id },
  )
  const certifications = userDetail?.data?.certifications?.sort((c1, c2) => {
    return Number(new Date(c2?.expiration)) - Number(new Date(c1?.expiration))
  })
  const validLevels = ['OL1', 'L1', 'L2', 'L3', 'L4']
  const userLevels =
    certifications
      ?.map((c) => c.level)
      .sort()
      .reverse()
      .filter((l) => validLevels.includes(l)) ?? []
  const highestLevel =
    userLevels[0] === 'OL1'
      ? userLevels?.length > 1
        ? userLevels[1]
        : 'OL1'
      : userLevels[0]
  const highestCredential = certifications?.find(
    (c) => c.level === highestLevel,
  )
  const credentialExpirationDays =
    differenceInDays(new Date(highestCredential?.expiration), new Date()) + 2
  const alert90Days =
    credentialExpirationDays <= 91 && affiliate?.roles?.includes(Role.LOR)
  const alertCredentialExpired =
    !highestCredential?.active ||
    (credentialExpirationDays < 1 && affiliate?.roles?.includes(Role.LOR))
  const alertCredentialExpiredGracePeriod =
    credentialExpirationDays <= 0 && credentialExpirationDays >= -31
  const affiliatePaymentDate = affiliate?.standingDetails?.paymentDueDate
  const alertPayment =
    affiliate?.standingDetails?.paymentStatus !==
      AffiliatePaymentStatus.Current &&
    affiliate?.standingDetails?.paymentStatus != null
  const pastDueInDays = differenceInDays(
    new Date(affiliatePaymentDate),
    new Date(),
  )
  const [didDismiss, setDidDismiss] = useState(false)
  const issuesCount =
    Number(didDismiss ? 0 : alert90Days) + Number(alertPayment)
  const issueString = `${issuesCount} issue${issuesCount > 1 ? 's' : ''}`

  const [showCredentialBanner, setShowCredentialBanner] = useState(alert90Days)
  const [showPaymentBanner, setShowPaymentBanner] = useState(alertPayment)
  const hideDetails = () => {
    setShowCredentialBanner(false)
    setShowPaymentBanner(false)
  }
  const showDetails = () => {
    setShowCredentialBanner(didDismiss ? false : alert90Days)
    setShowPaymentBanner(alertPayment)
  }
  const credentialLinkUrl = () => {
    switch (highestLevel) {
      case 'OL1':
        return 'https://www.crossfit.com/certificate-courses/level-2'
      case 'L1':
        return 'https://www.crossfit.com/certificate-courses/level-2'
      case 'L2':
        return 'https://www.crossfit.com/certificate-courses/level-2'
      case 'L3':
        return 'https://certifications.crossfit.com/ccft/continuing-education'
      case 'L4':
        return 'https://certifications.crossfit.com/ccft/continuing-education'
    }
  }

  const credentialDescription = () => {
    if (credentialExpirationDays > 0) {
      switch (highestLevel) {
        case 'OL1':
          return `Your trainer credential will expire in <b>${credentialExpirationDays} days </b>. Register for an upcoming Level 2 Certificate Course.`
        case 'L1':
          return `Your trainer credential will expire in <b>${credentialExpirationDays} days </b>. Register for an upcoming Level 2 Certificate Course.`
        case 'L2':
          return `Your trainer credential will expire in <b>${credentialExpirationDays} days </b>. Register for an upcoming Level 2 Certificate Course.`
        case 'L3':
          return `Your trainer credential will expire in <b>${credentialExpirationDays} days </b>. Learn about your Level 3 <a href='https://crossfit-jira.atlassian.net/wiki/spaces/AP/pages/494600376/Banner+Copy+Doc#'>maintenance requirements</a>, check your <a href='https://certifications.crossfit.com/ccft/activity-log'>current progress</a>, and register for <a href='https://certifications.crossfit.com/ccft/continuing-education'>CrossFit Training Courses<a/> today.`
        case 'L4':
          return `Your trainer credential will expire in <b>${credentialExpirationDays} days </b>. Learn about your Level 4 <a href='https://crossfit-jira.atlassian.net/wiki/spaces/AP/pages/494600376/Banner+Copy+Doc#'>maintenance requirements</a>, check your <a href='https://certifications.crossfit.com/ccft/activity-log'>current progress</a>, and register for <a href='https://certifications.crossfit.com/ccft/continuing-education'>CrossFit Training Courses<a/> today.`
      }
    }

    return `Your required trainer credential is expired. To maintain your status as a CrossFit affiliate, register for an upcoming Level 2 Certificate Course within the next ${
      30 + credentialExpirationDays
    } days.<br /><br/>

    Note: This issue will be resolved upon registering for a course (you do not need to have completed the course within ${
      30 + credentialExpirationDays
    } days).<br /><br/>
    
    If course registration is not complete in ${
      30 + credentialExpirationDays
    } days, your affiliate will no longer appear on the CrossFit map.`
  }

  const pastDueDescription = () => {
    return `Your affiliate fee payment is past due. To maintain your status as a CrossFit affiliate, visit the payment center and make a payment within the next ${
      30 + pastDueInDays
    } days.<br/><br/>

    If payment is not received in the next ${
      30 + pastDueInDays
    } days, your affiliate will no longer appear on the CrossFit map.`
  }

  return (
    <BannerContext.Provider
      value={{
        alert90Days,
        alertCredentialExpired,
      }}
    >
      {affiliate?.roles?.includes(Role.LOR) && (
        <div
          className={classNames(
            style.bannersContainer,
            showCredentialBanner && style.bannersOverlay,
          )}
        >
          {(alertCredentialExpiredGracePeriod || alertPayment) && (
            <div className={style.gracePeriodFlyer}>
              <div className={style.flyerLeft}>
                <Icon name='alert' className={style.flyerIcon} />
                <Text type='overline' size='sm' className={style.flyerTitle}>
                  {issueString}
                </Text>
              </div>
              <div className={style.flyerRight}>
                {(showCredentialBanner || showPaymentBanner) && (
                  <div onClick={hideDetails} className={style.details}>
                    <Text
                      type='overline'
                      size='sm'
                      className={style.flyerSubtitle}
                    >
                      Hide Details
                    </Text>
                    <img className={style.flyerIcon} src={Hide} />
                  </div>
                )}
                {!showCredentialBanner && !showPaymentBanner && (
                  <div onClick={showDetails} className={style.details}>
                    <Text
                      type='overline'
                      size='sm'
                      className={style.flyerSubtitle}
                    >
                      Show Details
                    </Text>
                    <Icon name='plus' className={style.flyerIcon} />
                  </div>
                )}
              </div>
            </div>
          )}
          {showPaymentBanner && (
            <CollapsibleBanner
              type='urgent'
              dismiss={hideDetails}
              linkText='Payment Center'
              linkUrl='https://affiliate.crossfit.com/affiliate-portal/payment-center'
              title='Past Due Payment'
              description={pastDueDescription()}
            />
          )}
          {showCredentialBanner && (
            <CollapsibleBanner
              type={alertCredentialExpired ? 'urgent' : 'nonurgent'}
              dismiss={() => {
                setShowCredentialBanner(false)
                setDidDismiss(true)
              }}
              linkText='Courses Near Me'
              linkUrl={credentialLinkUrl()}
              title={
                credentialExpirationDays > 0
                  ? 'Credential  Expiring Soon'
                  : 'Credential Expired'
              }
              description={credentialDescription()}
            />
          )}
        </div>
      )}

      {children}
    </BannerContext.Provider>
  )
}
